import type { AssetTypeDetail } from "@/entities/asset-type";
import type { ComponentTreeTranslation } from "@/entities/component-tree";
import type { DefectTree } from "@/entities/defect-tree";
import type { Nullable } from "@/types";

import { useEffect, useState } from "react";

import { extractDefectTreeComponentsKeys } from "@/components/AssetType/AssetTypeFormHelpers";
import { useReferentialLanguages } from "@/entities/referential/referential-languages";
import { useUpdate } from "@/hooks/queries";
import { useApiErrorHandler } from "@/modules/api-errors";
import {
  useAppSaveBar,
  useAppSaveBarHandler,
  useAppSaveBarSync,
} from "@/modules/app-save-bar";
import { removeEmptyValuesFromObject } from "@/utilities/object";

import { ASSET_TYPE_SEVERITY_GRID_SCOPE } from "./asset-type-severity-grid-tab.constant";

export const useAssetTypeSeverityGrid = (assetType?: AssetTypeDetail) => {
  const SCOPE = ASSET_TYPE_SEVERITY_GRID_SCOPE;

  const { mutate, isPending } = useUpdate("assetTypes", assetType?.id ?? 0);
  const { setSaveBar } = useAppSaveBar(SCOPE);
  const { onError } = useApiErrorHandler();

  const initDefectTree = assetType?.defect_tree;
  const initTranslation = assetType?.component_tree_translation;

  const { languages } = useReferentialLanguages();

  const [resetKey, setResetKey] = useState(0);
  const [defectTree, setDefectTree] = useState<Nullable<DefectTree>>();
  const [translation, setTranslation] =
    useState<Nullable<ComponentTreeTranslation | undefined>>(initTranslation);

  useEffect(() => {
    setDefectTree(initDefectTree);
  }, [initDefectTree]);

  useEffect(() => {
    setTranslation(initTranslation);
  }, [initTranslation]);

  const onUpdateDefectTree = (
    modifiedDefectTree: string,
    isRegistrable: boolean
  ) => {
    try {
      const json = JSON.parse(modifiedDefectTree);
      setDefectTree(json);
      cleanUpTranslations(json);
      if (isRegistrable) setSaveBar("dirty", true);
    } catch (_exception) {
      // No need to update defect tree if json is not valid
    }
  };

  const onUpdateTranslation = (
    modifiedTranslation: string,
    isRegistrable: boolean
  ) => {
    try {
      const json = JSON.parse(modifiedTranslation);
      setTranslation(json);
      if (isRegistrable) setSaveBar("dirty", true);
    } catch (_exception) {
      // No need to update translation if json is not valid
    }
  };

  const cleanUpTranslations = (tree: DefectTree) => {
    const nonEmptyTranslations = removeEmptyValuesFromObject(
      translation ?? {}
    ) as Record<string, Record<string, string>>;

    const keys = extractDefectTreeComponentsKeys(tree);

    const output = languages.map(({ code }) => {
      return [
        code,
        Object.assign(
          {},
          Object.fromEntries(keys.map((key: string) => [key, ""])),
          nonEmptyTranslations[code] ?? {}
        ),
      ];
    });

    const newTranslations = Object.fromEntries(output);

    setTranslation(newTranslations);
  };

  const onSave = () => {
    const isDefectTreeEmpty = Object.keys(defectTree ?? {}).length === 0;

    const data = {
      defect_tree: isDefectTreeEmpty ? null : defectTree,
      component_tree_translation: translation,
    };

    mutate(data, {
      onSuccess: () => setSaveBar("dirty", false),
      onError,
    });
  };

  const onCancel = () => {
    setDefectTree(initDefectTree);
    setTranslation(initTranslation);
    setSaveBar("dirty", false);
    setResetKey((k) => k + 1); // force editor remount
  };

  // Sync save bar
  useAppSaveBarHandler(SCOPE, { onSave, onCancel });
  useAppSaveBarSync(SCOPE, "saving", isPending);

  return {
    defectTree,
    translation,
    resetKey,
    onUpdateDefectTree,
    onUpdateTranslation,
  };
};
